import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';

export const DataResetDangerZone = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = user?.role_name === 'HQ Super Admin';

  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmWord, setConfirmWord] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/system/reset/modules');
      setModules(res.data?.modules || []);
    } catch (e) {
      console.error('Failed to load reset modules', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const toggle = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectedDocs = modules
    .filter(m => selected.includes(m.key))
    .reduce((sum, m) => sum + m.total_documents, 0);

  const handleReset = async () => {
    try {
      setSubmitting(true);
      const res = await api.post('/system/reset/selective', {
        modules: selected,
        confirmation: confirmWord.trim()
      });
      toast({
        title: 'Reset complete',
        description: `${res.data.total_deleted} records deleted across ${selected.length} module(s)`
      });
      setConfirmOpen(false);
      setConfirmWord('');
      setSelected([]);
      fetchModules();
    } catch (e) {
      toast({
        title: 'Reset failed',
        description: e.response?.data?.detail || 'Could not complete the reset',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-red-200" data-testid="data-reset-danger-zone">
      <CardHeader className="bg-red-50 border-b border-red-100">
        <CardTitle className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" /> Danger Zone &mdash; Data Reset
        </CardTitle>
        <CardDescription className="text-red-600">
          Permanently deletes the selected data for the whole system. This cannot be undone.
          Download a backup from System Settings first.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {!isSuperAdmin && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
               data-testid="reset-permission-warning">
            Only an HQ Super Admin can run a data reset.
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label className="text-gray-700">Select what to reset</Label>
          <Button variant="outline" size="sm" onClick={fetchModules} data-testid="refresh-reset-modules-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh counts
          </Button>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-red-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="reset-modules-list">
            {modules.map(m => (
              <label
                key={m.key}
                htmlFor={`reset-module-${m.key}`}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selected.includes(m.key)
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-red-200 hover:bg-red-50/40'
                }`}
              >
                <Checkbox
                  id={`reset-module-${m.key}`}
                  checked={selected.includes(m.key)}
                  onCheckedChange={() => toggle(m.key)}
                  disabled={!isSuperAdmin}
                  data-testid={`reset-module-checkbox-${m.key}`}
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-800">{m.label}</span>
                    <span className="text-xs text-gray-500" data-testid={`reset-module-count-${m.key}`}>
                      {m.total_documents} records
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{m.description}</p>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <p className="text-sm text-gray-600" data-testid="reset-selection-summary">
            {selected.length} module(s) selected &middot; <strong>{selectedDocs}</strong> records will be deleted
          </p>
          <Button
            variant="destructive"
            disabled={!isSuperAdmin || selected.length === 0}
            onClick={() => setConfirmOpen(true)}
            data-testid="open-reset-confirm-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Reset Selected Data
          </Button>
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-white" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Confirm data reset
            </DialogTitle>
            <DialogDescription>
              You are about to permanently delete <strong>{selectedDocs}</strong> records from:
            </DialogDescription>
          </DialogHeader>

          <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1 max-h-40 overflow-y-auto">
            {modules.filter(m => selected.includes(m.key)).map(m => (
              <li key={m.key}>{m.label} <span className="text-gray-400">({m.total_documents})</span></li>
            ))}
          </ul>

          <div className="space-y-2">
            <Label className="text-gray-700">Type <strong>RESET</strong> to confirm</Label>
            <Input
              value={confirmWord}
              onChange={(e) => setConfirmWord(e.target.value)}
              placeholder="RESET"
              data-testid="reset-confirm-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="reset-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirmWord.trim() !== 'RESET' || submitting}
              onClick={handleReset}
              data-testid="reset-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DataResetDangerZone;
